import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Graph } from '@shared/model/api';
import { Dataset } from '@shared/model/dataset';
import { ProfileStore } from '@shared/model/profile';
import { fetchAllProfiles, fetchGraph, fetchProfile } from '../api/client';

export interface DatasetContextValue {
    status: 'loading' | 'ready' | 'error';
    error?: string;
    graph?: Graph;
    dataset?: Dataset;
    profiles: ProfileStore;
    /** Incremented every time profiles are added (profiles is mutable). */
    profilesVersion: number;
    /** Loads the elevation profiles of the given routes (no-op for the ones already loaded). */
    loadProfiles: (routeIds: Iterable<string>) => Promise<void>;
    /** Loads every profile in one request (map of all trails). */
    loadAllProfiles: () => Promise<void>;
}

const DatasetContext = createContext<DatasetContextValue | undefined>(undefined);

interface ProviderProps {
    children: ReactNode;
    /** Pre-loaded data (tests). */
    initialGraph?: Graph;
}

export function DatasetProvider({ children, initialGraph }: ProviderProps) {
    const [graph, setGraph] = useState<Graph | undefined>(initialGraph);
    const [error, setError] = useState<string | undefined>();
    const [profilesVersion, setProfilesVersion] = useState(0);
    const profiles = useMemo(() => new ProfileStore(), []);
    const pending = useRef(new Map<string, Promise<void>>());
    const allLoaded = useRef<Promise<void> | undefined>(undefined);

    useEffect(() => {
        if (graph) return;
        let cancelled = false;
        fetchGraph()
            .then((g) => {
                if (!cancelled) setGraph(g);
            })
            .catch((e: unknown) => {
                if (!cancelled) setError(e instanceof Error ? e.message : String(e));
            });
        return () => {
            cancelled = true;
        };
    }, [graph]);

    const dataset = useMemo(() => (graph ? new Dataset(graph) : undefined), [graph]);

    const loadProfiles = useCallback(
        async (routeIds: Iterable<string>) => {
            const tasks: Promise<void>[] = [];
            for (const id of routeIds) {
                if (profiles.has(id)) continue;
                let task = pending.current.get(id);
                if (!task) {
                    task = fetchProfile(id)
                        .then((profile) => {
                            profiles.set(id, profile);
                        })
                        .finally(() => pending.current.delete(id));
                    pending.current.set(id, task);
                }
                tasks.push(task);
            }
            if (tasks.length === 0) return;
            await Promise.all(tasks);
            setProfilesVersion((v) => v + 1);
        },
        [profiles],
    );

    const loadAllProfiles = useCallback(async () => {
        if (!allLoaded.current) {
            allLoaded.current = fetchAllProfiles()
                .then((all) => {
                    for (const [id, profile] of Object.entries(all)) if (!profiles.has(id)) profiles.set(id, profile);
                    setProfilesVersion((v) => v + 1);
                })
                .catch((e: unknown) => {
                    allLoaded.current = undefined;
                    throw e;
                });
        }
        await allLoaded.current;
    }, [profiles]);

    const value = useMemo<DatasetContextValue>(
        () => ({
            status: error ? 'error' : dataset ? 'ready' : 'loading',
            error,
            graph,
            dataset,
            profiles,
            profilesVersion,
            loadProfiles,
            loadAllProfiles,
        }),
        [error, graph, dataset, profiles, profilesVersion, loadProfiles, loadAllProfiles],
    );

    return <DatasetContext.Provider value={value}>{children}</DatasetContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useDataset(): DatasetContextValue {
    const value = useContext(DatasetContext);
    if (!value) throw new Error('useDataset must be used inside <DatasetProvider>');
    return value;
}
